import type { StoreApi } from 'zustand';
import { clearPersistedStores } from './persistence';

type ResettableState = { reset: () => void };

/**
 * Compile-time registry of every store, keyed by the name it is registered
 * under. Each store augments this interface with `name: typeof theStore` next
 * to its `registerStore` call, so the name and the store's own type are the
 * single source of truth: `registerStore`, `preloadStores`, and the test
 * `preloadedState` render option all derive from here. A typo in a name or a
 * mismatch against the store's state shape is a type error, not a runtime one.
 *
 * @example
 * // in issueBuilderStore.ts
 * declare module './registry' {
 *   interface StoreRegistry {
 *     issueBuilder: typeof issueBuilderStore;
 *   }
 * }
 */
// biome-ignore lint/suspicious/noEmptyInterface: augmentation target — must stay an interface for `declare module` merging
export interface StoreRegistry {}

type StateOf<T> = T extends StoreApi<infer S> ? S : never;

/** Names of all registered stores. */
export type StoreName = keyof StoreRegistry;

/**
 * Partial state per store, derived from each store's own type. Passing a name
 * that isn't registered, or a field the store doesn't hold, is a type error.
 */
export type PreloadedState = {
  [K in StoreName]?: Partial<StateOf<StoreRegistry[K]>>;
};

const registry = new Map<StoreName, StoreApi<ResettableState>>();

/**
 * Registers a Zustand store under a stable name so it can be reset between
 * tests / on logout via `resetAllStores`, and seeded in the test environment
 * via `preloadStores`. The name must be declared in `StoreRegistry` and the
 * store must expose a `reset` action.
 */
export const registerStore = <K extends StoreName>(name: K, store: StoreRegistry[K] & StoreApi<ResettableState>) => {
  // The map is keyed by the registered name; the narrowing keeps it free of
  // `any` while `getState().reset()` stays fully typed.
  registry.set(name, store as unknown as StoreApi<ResettableState>);
};

/**
 * Resets every registered store back to its initial state. Called on logout
 * and after each test.
 */
export const resetAllStores = () => {
  for (const store of registry.values()) {
    store.getState().reset();
  }
};

/**
 * Tears every store down for logout: synchronously resets in-memory state (so
 * the UI reflects the cleared state immediately) and returns a promise that
 * resolves once the persisted localforage/IndexedDB databases are wiped. Await
 * the returned promise to keep the durable teardown from racing the rest of the
 * logout flow.
 */
export const clearAllStores = () => {
  resetAllStores();
  return clearPersistedStores();
};

/**
 * Seeds registered stores with partial state. Intended for the test
 * environment, mirroring the old Redux `preloadedState`/`initialState` helper.
 *
 * Seeding is a shallow merge (Zustand's default `setState`): a nested object in
 * `state` replaces its counterpart wholesale rather than being deep-merged, so
 * pass the full nested value you want, not a partial slice of it.
 *
 * @example
 * preloadStores({ issueBuilder: { builderIssueId: 'draft-id' } });
 */
export const preloadStores = (preloadedState: PreloadedState) => {
  for (const [name, state] of Object.entries(preloadedState) as [StoreName, object][]) {
    const store = registry.get(name);
    if (!store) {
      throw new Error(
        `preloadStores: no store registered under "${name}". Did you call registerStore("${name}", ...)?`
      );
    }
    store.setState(state as Partial<ResettableState>);
  }
};

/**
 * Clears the store registry. Test-only seam so specs that register throwaway
 * stores don't leak them into the shared singleton.
 */
export const clearRegistry = () => {
  registry.clear();
};
